import React, { useState } from "react";
import { ethers } from "ethers";

// Minimal ABI for the functions we need
const OPERATOR_ABI = [
  "function addToWhitelist(address operator)",
  "function setValidAction(bool valid, uint256 duration)"
];

const OPERATOR_CONTRACT_ADDRESS = "0xF31c260Df287106B3F86e72D8935943Ba95A36b9";

const Other = ({ signer }) => {
  const [address, setAddress] = useState("");
  const [, setJsonFile] = useState(null);
  const [jsonContent, setJsonContent] = useState(null);
  const [operator, setOperator] = useState("");
  const [duration, setDuration] = useState(30); // Default to 30 days (1 month)
  const [jsonError, setJsonError] = useState("");
  const [operatorError, setOperatorError] = useState("");
  const [success, setSuccess] = useState("");
  const [filename, setFilename] = useState("processed_blueprint");

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setJsonFile(file);
    setJsonError("");
    setSuccess("");
    
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = JSON.parse(e.target.result);
          setJsonContent(content);
        } catch (err) {
          setJsonError("Invalid JSON file");
        }
      };
      reader.readAsText(file);
    }
  };

  const handleAddressChange = (e) => {
    let value = e.target.value;
    // Add 0x prefix if not present
    if (value && !value.startsWith("0x")) {
      value = "0x" + value;
    }
    setAddress(value);
    setJsonError("");
  };

  const handleProcessJson = () => {
    setJsonError("");
    setSuccess("");

    // Validate address
    if (!address) {
      setJsonError("Please enter an address");
      return;
    }

    if (!ethers.isAddress(address)) {
      setJsonError("Invalid Ethereum address");
      return;
    }

    if (!jsonContent) {
      setJsonError("Please upload a JSON file");
      return;
    }

    try {
      // Clone the JSON content
      const processedJson = { ...jsonContent };
      
      // Find and replace the address in the data field
      const data = processedJson.data;
      const targetBytes = "b388930b";
      
      // Find the position of the target bytes
      const index = data.indexOf(targetBytes);
      
      if (index === -1) {
        setJsonError("Target bytes 'b388930b' not found in data");
        return;
      }
      
      // The address starts after the 4-byte selector (8 hex chars)
      const addressStartIndex = index + 8;
      
      // Remove '0x' prefix from address for replacement
      const cleanAddress = address.toLowerCase().replace("0x", "");
      
      // Pad address to 32 bytes (64 hex chars)
      const paddedAddress = cleanAddress.padStart(64, "0");
      
      // Replace the 32 bytes (64 hex chars) after the selector
      const newData = data.substring(0, addressStartIndex) + 
                     paddedAddress + 
                     data.substring(addressStartIndex + 64);
      
      processedJson.data = newData;
      
      // Download the modified JSON
      const jsonStr = JSON.stringify(processedJson, null, 2);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filename}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSuccess("JSON processed successfully! File downloaded.");
    } catch (err) {
      setJsonError("Error processing JSON: " + err.message);
    }
  };

  const handleWhitelistOperator = async () => {
    setOperatorError("");
    
    if (!operator) {
      setOperatorError("Please enter an operator address");
      return;
    }

    if (!ethers.isAddress(operator)) {
      setOperatorError("Invalid operator address");
      return;
    }

    if (!signer) {
      setOperatorError("Please connect your wallet first");
      return;
    }

    try {
      const contract = new ethers.Contract(OPERATOR_CONTRACT_ADDRESS, OPERATOR_ABI, signer);
      console.log("Whitelisting operator:", operator);
      
      const tx = await contract.addToWhitelist(operator);
      console.log("Transaction sent:", tx.hash);
      
      await tx.wait();
      console.log("Transaction confirmed");
      
    } catch (err) {
      setOperatorError("Error whitelisting operator: " + err.message);
    }
  };

  const handleInitiateValidator = async () => {
    setOperatorError("");

    if (!signer) {
      setOperatorError("Please connect your wallet first");
      return;
    }

    if (!duration || duration <= 0) {
      setOperatorError("Please enter a valid duration (must be greater than 0 days)");
      return;
    }

    try {
      const contract = new ethers.Contract(OPERATOR_CONTRACT_ADDRESS, OPERATOR_ABI, signer);

      // Get current block timestamp
      const provider = signer.provider;
      const block = await provider.getBlock("latest");
      const currentTimestamp = block.timestamp;

      // Calculate duration in seconds from days
      const durationInSeconds = duration * 24 * 60 * 60;
      const validUntilTimestamp = currentTimestamp + durationInSeconds;

      console.log("Current block timestamp:", currentTimestamp);
      console.log(`Setting valid action for ${duration} days until timestamp:`, validUntilTimestamp);

      const tx = await contract.setValidAction(true, validUntilTimestamp);
      console.log("Transaction sent:", tx.hash);

      await tx.wait();
      console.log("Transaction confirmed");

    } catch (err) {
      setOperatorError("Error setting operator duration: " + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <h1 className="text-3xl font-bold text-center text-green-400 mb-6">Other</h1>
      
      <div className="flex flex-col lg:flex-row gap-6">
        {/* JSON Processing Box */}
        <div className="flex-1 bg-gray-800 p-6 rounded-md">
          <h2 className="text-xl font-semibold text-green-400 mb-4">JSON File Processing</h2>
          
          {jsonError && (
            <div className="mb-4 p-3 bg-red-600 bg-opacity-20 border border-red-600 rounded-md text-red-400">
              {jsonError}
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-3 bg-green-600 bg-opacity-20 border border-green-600 rounded-md text-green-400">
              {success}
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-white mb-2">
              Address
            </label>
            <input
              type="text"
              value={address}
              onChange={handleAddressChange}
              placeholder="0x000..."
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-green-400 focus:outline-none font-mono"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-white mb-2">
              Import JSON File
            </label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileChange}
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-green-400 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-green-400 file:text-gray-900 hover:file:bg-green-300"
            />
          </div>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="Filename"
              className="flex-1 p-3 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-green-400 focus:outline-none"
            />
            <button
              onClick={handleProcessJson}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white p-3 rounded-md font-semibold"
            >
              Process JSON and Download File
            </button>
          </div>
        </div>

        {/* Approve Operator Box */}
        <div className="flex-1 bg-gray-800 p-6 rounded-md">
          <h2 className="text-xl font-semibold text-green-400 mb-4">Approve Operator</h2>

          {operatorError && (
            <div className="mb-4 p-3 bg-red-600 bg-opacity-20 border border-red-600 rounded-md text-red-400">
              {operatorError}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-white mb-2">
              Operator Address
            </label>
            <input
              type="text"
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
              placeholder="Enter operator address"
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-green-400 focus:outline-none"
            />
          </div>

          <div className="mb-4">
            <button
              onClick={handleWhitelistOperator}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white p-3 rounded-md font-semibold"
            >
              Whitelist Operator
            </button>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-white mb-2">
              Duration (days)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                placeholder="Duration in days"
                min="1"
                className="flex-1 p-3 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-green-400 focus:outline-none"
              />
              <button
                onClick={handleInitiateValidator}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-md font-semibold"
              >
                Set Operator Active Duration
              </button>
            </div>
          </div>

          <p className="text-sm text-gray-400 text-center">
            1) Whitelist the operator, then 2) set a duration for how long the operator is active. If the operator is still active, updating the duration will override the current duration.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Other;